import { Link } from '@tanstack/react-router'
import { Sparkles, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { PageContainer, PageBody } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'

/**
 * Full-page placeholder for a route gated behind a feature flag whose value
 * is currently OFF. The page mounts so deep links don't 404, but the body
 * makes it clear the surface isn't enabled yet and points the user back to
 * a working entrypoint.
 */
interface GatedFeaturePlaceholderProps {
  eyebrow?: string
  title: string
  description: string
  icon?: LucideIcon
  /** Flag name (env var key) displayed for ops/support. */
  flagName: string
  /** Where the "Back to safety" link should go. Defaults to /dashboard. */
  backTo?: '/dashboard' | '/docfiller'
}

export function GatedFeaturePlaceholder({
  eyebrow = 'Coming soon',
  title,
  description,
  icon = Sparkles,
  flagName,
  backTo = '/dashboard',
}: GatedFeaturePlaceholderProps) {
  return (
    <PageContainer width="narrow">
      <PageHeader eyebrow={eyebrow} title={title} description={description} />
      <PageBody>
        <EmptyState
          icon={icon}
          title="This area is not enabled yet"
          description={
            <span>
              The <strong>{flagName}</strong> feature flag is currently off in
              this environment. Existing flows are unaffected — pages light up
              automatically once the flag is enabled.
            </span>
          }
          action={
            <Button asChild variant="outline" size="sm">
              <Link to={backTo}>Back to {backTo === '/docfiller' ? 'Doc Filler' : 'Dashboard'}</Link>
            </Button>
          }
        />
      </PageBody>
    </PageContainer>
  )
}
